import { useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import { useTitle } from 'react-use';

import { ReadOnlyBanner } from '@/components/atoms/ReadOnlyBanner/ReadOnlyBanner';
import { PageSubHeader, PageSubHeaderActions } from '@/components/organisms/PageSubHeader';
import { ResponsiveSidebarPanel } from '@/components/organisms/Sidebar/ResponsiveSidebarPanel';
import { Button } from '@/components/ui/button';
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@/components/ui/resizable';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectAccountSlug } from '@/redux/session/selectors';
import useSegments from '@/segment/useSegments';
import { actions } from '@/shared/types/permissions';
import { PageName } from '@/shared/types/segment';
import { RootState } from '@/shared/types/state';

import { ActivityTab } from '../components/ActivityTab';
import { ContactAsOfPanel } from '../components/ContactAsOfPanel';
import { ContactBreadcrumbs } from '../components/ContactBreadcrumbs';
import { ContactFormPanel } from '../components/ContactFormPanel';
import { ContactSidebar } from '../components/ContactSidebar';
import { DebtsTab } from '../components/DebtsTab';
import { MemoriesTab } from '../components/MemoriesTab';
import { SendMessageToContactDialog } from '../components/SendMessageToContactDialog';
import { useContact } from '../hooks/useContact';
import { useContactDebts } from '../hooks/useContactDebts';
import { useContactIdentities } from '../hooks/useContactIdentities';

const CONTACT_TABS = ['memories', 'activity', 'debts'] as const;
type ContactTabKey = (typeof CONTACT_TABS)[number];

const isContactTab = (value?: string): value is ContactTabKey =>
  !!value && (CONTACT_TABS as readonly string[]).includes(value);

export const ContactDetailPage = () => {
  const { contactId, tab } = useParams<{ contactId: string; tab: string }>();

  useSegments(PageName.CONTACT, contactId);

  const { contact, status } = useContact(contactId);
  const {
    identities,
    status: identitiesStatus,
    refetch: refetchIdentities,
  } = useContactIdentities(contactId);
  const { total: debtCount } = useContactDebts(contactId);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const slug = useSelector(selectAccountSlug);
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [asOfAt, setAsOfAt] = useState<string | null>(null);
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'contacts', actions.WRITE)
  );
  const hasDebts = debtCount > 0;

  const pageTitle = contact
    ? t('pages.contact', { 0: contact.name?.trim() || t('contacts.unnamed') })
    : t('pages.contacts');
  useTitle(pageTitle);

  // The active tab is driven by the URL (`/contacts/:id/:tab`) so it is
  // linkable and survives reload / back-forward. Unknown segments fall back
  // to the activity tab.
  const activeTab: ContactTabKey = isContactTab(tab) ? tab : 'activity';

  const handleTabChange = (value: string) => {
    if (slug && contactId) {
      // Tabs are a view filter on one contact, not navigation, so replace the
      // history entry instead of pushing. This keeps the URL deep-linkable
      // while leaving the Back button to return to wherever the user came from
      // (e.g. the contacts list) rather than stepping through each tab.
      navigate(`/${slug}/contacts/${contactId}/${value}`, { replace: true });
    }
  };

  // `useContactIdentities` already drops soft-deleted rows, so a method removed
  // through the panel stops counting towards messageability here.
  const hasReachableIdentity = identities.some(
    (identity) => identity.type === 'phone' || identity.type === 'email'
  );

  if (status === 'pending') {
    return (
      <div className="flex h-full min-h-0 flex-col">
        <PageSubHeader breadcrumbs={<ContactBreadcrumbs />} />
        <div className="mx-auto w-full max-w-(--layout-max-width) flex-1 overflow-y-auto px-4 pt-4 lg:px-5 xl:px-6 2xl:px-8">
          <div className="flex gap-6">
            <Skeleton className="h-96 w-96 shrink-0" />
            <Skeleton className="h-96 flex-1" />
          </div>
        </div>
      </div>
    );
  }

  if (status === 'error' || !contact) {
    return (
      <div className="flex h-full min-h-0 flex-col">
        <PageSubHeader breadcrumbs={<ContactBreadcrumbs />} />
        <div className="mx-auto w-full max-w-(--layout-max-width) flex-1 overflow-y-auto px-4 pt-4 lg:px-5 xl:px-6 2xl:px-8">
          <p className="text-muted-foreground">{t('contacts.not_found')}</p>
        </div>
      </div>
    );
  }

  return (
    <ResizablePanelGroup
      direction="horizontal"
      className="h-full w-full"
      autoSaveId="contact-detail-edit"
    >
      <ResizablePanel order={1} className="flex min-h-0 flex-col overflow-hidden">
        <div className="flex h-full min-h-0 flex-col">
          <PageSubHeader
            breadcrumbs={<ContactBreadcrumbs contactName={contact.name} />}
            actions={
              hasReachableIdentity ? (
                <PageSubHeaderActions>
                  <Button size="sm" onClick={() => setMessageDialogOpen(true)}>
                    {t('contacts.message')}
                  </Button>
                </PageSubHeaderActions>
              ) : undefined
            }
          />
          {!canWrite && <ReadOnlyBanner />}
          <div className="mx-auto flex min-h-0 w-full max-w-(--layout-max-width) flex-1 gap-6 px-4 pt-4 pb-8 lg:px-5 xl:px-6 2xl:px-8">
            <div className="scrollbar-hide w-96 shrink-0 overflow-y-auto">
              <ContactSidebar
                contact={contact}
                onSendMessage={hasReachableIdentity ? () => setMessageDialogOpen(true) : undefined}
                onEdit={
                  canWrite
                    ? () => {
                        setAsOfAt(null);
                        setIsEditOpen(true);
                      }
                    : undefined
                }
              />
            </div>
            <div className="min-w-0 flex-1 overflow-y-auto">
              <div className="min-w-128">
                <Tabs
                  value={activeTab}
                  onValueChange={handleTabChange}
                  className="w-full max-w-2xl"
                >
                  <TabsList variant="line" className="sticky top-0 z-10 w-full bg-background">
                    <TabsTrigger variant="line" value="memories">
                      {t('contacts.memories')}
                    </TabsTrigger>
                    <TabsTrigger variant="line" value="activity">
                      {t('contacts.activity')}
                    </TabsTrigger>
                    {(hasDebts || activeTab === 'debts') && (
                      <TabsTrigger variant="line" value="debts">
                        {t('contacts.debts.tab')}
                      </TabsTrigger>
                    )}
                  </TabsList>
                  <TabsContent value="memories" className="focus-visible:ring-0">
                    <MemoriesTab contactId={contact.contact_id} />
                  </TabsContent>
                  <TabsContent value="activity" className="focus-visible:ring-0">
                    <ActivityTab
                      contactId={contact.contact_id}
                      onShowAsOf={isEditOpen ? undefined : setAsOfAt}
                    />
                  </TabsContent>
                  {(hasDebts || activeTab === 'debts') && (
                    <TabsContent value="debts" className="focus-visible:ring-0">
                      <DebtsTab contactId={contact.contact_id} />
                    </TabsContent>
                  )}
                </Tabs>
              </div>
            </div>
          </div>
          {hasReachableIdentity && (
            <SendMessageToContactDialog
              open={messageDialogOpen}
              onOpenChange={setMessageDialogOpen}
              contact={contact}
              identities={identities}
            />
          )}
        </div>
      </ResizablePanel>

      {/* Gate the edit form on a loaded identity list. Mounting it with an empty
          list (still loading, or failed) would snapshot blank rows and hide the
          contact's real methods, so a save could delete them all. A failed load
          gets a retry instead of a form. */}
      {isEditOpen && identitiesStatus !== 'pending' && (
        <>
          <ResizableHandle />
          <ResizablePanel
            order={2}
            defaultSize={28}
            minSize={24}
            maxSize={40}
            className="bg-background"
          >
            {identitiesStatus === 'error' ? (
              <div className="flex h-full flex-col items-start gap-3 p-4">
                <p className="text-sm leading-5 text-muted-foreground">
                  {t('contacts.errors.identities_load_failed')}
                </p>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => refetchIdentities()}>
                    {t('common.try_again')}
                  </Button>
                  <Button size="sm" variant="ghost-neutral" onClick={() => setIsEditOpen(false)}>
                    {t('common.cancel')}
                  </Button>
                </div>
              </div>
            ) : (
              // Keyed by contact: the panel freezes its identities, default
              // values and dirty-guard baseline on mount, and this route reuses
              // the same component instance when only `:contactId` changes.
              // Nothing navigates contact-to-contact today, but without the key
              // the first link that does would submit one contact's methods
              // against another's id.
              <ContactFormPanel
                key={contact.contact_id}
                mode="edit"
                contact={contact}
                identities={identities}
                onClose={() => setIsEditOpen(false)}
              />
            )}
          </ResizablePanel>
        </>
      )}

      {asOfAt && !isEditOpen && (
        <ResponsiveSidebarPanel title={t('contacts.as_of.open')} onClose={() => setAsOfAt(null)}>
          <ContactAsOfPanel contactId={contact.contact_id} at={asOfAt} />
        </ResponsiveSidebarPanel>
      )}
    </ResizablePanelGroup>
  );
};
